/**
 * Jobs Service
 * Handles job board API calls, worker profiles, company management, and applications
 */

import { telegram } from './telegram';

const JOBS_API = '/api/jobs';
const COMPANIES_API = '/api/companies';
const WORKERS_API = '/api/workers';

// ============================================================================
// Types
// ============================================================================

export interface Job {
  id: string;
  title: string;
  company: string;
  company_id?: string;
  description: string;
  location: string;
  basin: string;
  county?: string;
  jobType: string;
  payRange?: string;
  salary_min?: number;
  salary_max?: number;
  salary_type?: string;
  contactInfo: string;
  status: 'active' | 'closed' | 'filled';
  createdAt: string;
  expiresAt: string;
  postedBy: string;
  posterUsername?: string;
  views_count?: number;
  applications_count?: number;
  required_certifications?: string[];
  required_experience_years?: number;
}

export interface JobApplication {
  id: string;
  jobId: string;
  userId: string;
  message?: string;
  resume?: string;
  status: 'pending' | 'viewed' | 'contacted' | 'rejected' | 'withdrawn';
  createdAt: string;
  job?: Job;
}

export interface Company {
  id: string;
  name: string;
  email?: string;
  email_domain?: string;
  status: 'pending' | 'verified' | 'suspended';
  website?: string;
  description?: string;
  phone?: string;
  logo_url?: string;
  verified_at?: string;
  activeJobCount?: number;
}

export interface WorkerProfile {
  id?: string;
  telegram_user_id?: string;
  full_name: string;
  phone?: string;
  email?: string;
  job_title?: string;
  years_experience: number;
  certifications: string[];
  equipment: string[];
  skills: string[];
  preferred_counties: string[];
  preferred_basins: string[];
  availability: 'available' | 'employed' | 'not-looking';
  work_types: string[];
  willing_to_travel: boolean;
  travel_radius?: number;
  resume_url?: string;
  profile_visibility: 'public' | 'private';
  about?: string;
  parsed_resume_data?: any;
  telegramUsername?: string;
}

export interface JobSearchFilters {
  q?: string;
  basin?: string;
  county?: string;
  jobType?: string;
  certifications?: string[];
  limit?: number;
  offset?: number;
}

export interface WorkerSearchFilters {
  q?: string;
  certifications?: string[];
  skills?: string[];
  counties?: string[];
  basins?: string[];
  availability?: string;
  minExperience?: number;
  limit?: number;
  offset?: number;
}

// Oilfield certification options
export const OILFIELD_CERTIFICATIONS = [
  'SafeLand/SafeGulf',
  'H2S Alive',
  'PEC Basic',
  'TWIC Card',
  'CDL Class A',
  'CDL Class B',
  'Confined Space Entry',
  'Fall Protection',
  'First Aid/CPR',
  'Forklift Operator',
  'Crane Operator',
  'OSHA 10',
  'OSHA 30',
  'RigPass',
  'Well Control (IADC)',
  'Hot Work Permit',
  'Hazmat',
  'Respiratory Protection'
];

// Oilfield equipment options
export const OILFIELD_EQUIPMENT = [
  'Pickup Truck',
  'Service Truck',
  'Welding Rig',
  'Hot Shot Truck',
  'Crane',
  'Forklift',
  'Man Lift',
  'Backhoe',
  'Excavator',
  'Pump Truck',
  'Vacuum Truck',
  'Water Truck',
  'Trailer'
];

// Job types
export const JOB_TYPES = [
  'Full-time',
  'Part-time',
  'Contract',
  'Temporary',
  'Per Diem',
  'Rotation'
];

// Basins
export const BASINS = [
  'Permian Basin',
  'Delaware Basin',
  'Midland Basin',
  'San Juan Basin'
];

// Counties (SE New Mexico focus)
export const COUNTIES = [
  'Eddy County',
  'Lea County',
  'Chaves County',
  'Roosevelt County',
  'Loving County',
  'Ward County',
  'Winkler County',
  'Reeves County'
];

// ============================================================================
// Service Class
// ============================================================================

class JobsService {
  private getUserHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-Telegram-User-Id': telegram.getUserId() || '',
      'X-Telegram-Init-Data': telegram.getInitData()
    };
  }

  private getTelegramUserId(): string {
    return telegram.getUserId() || '';
  }

  // ==========================================================================
  // Jobs API
  // ==========================================================================

  /**
   * Search/list jobs
   */
  async searchJobs(filters: JobSearchFilters = {}): Promise<{ jobs: Job[]; total: number }> {
    try {
      const params = new URLSearchParams();
      if (filters.q) params.append('q', filters.q);
      if (filters.basin) params.append('basin', filters.basin);
      if (filters.county) params.append('county', filters.county);
      if (filters.jobType) params.append('type', filters.jobType);
      if (filters.certifications?.length) {
        params.append('certifications', filters.certifications.join(','));
      }
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${JOBS_API}?${params}`);
      const data = await response.json();

      return {
        jobs: data.jobs || [],
        total: data.count || data.total || 0
      };
    } catch (error) {
      console.error('Job search error:', error);
      return { jobs: [], total: 0 };
    }
  }

  /**
   * Get job by ID
   */
  async getJob(jobId: string): Promise<Job | null> {
    try {
      const response = await fetch(`${JOBS_API}/${jobId}`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.job || data;
    } catch (error) {
      console.error('Get job error:', error);
      return null;
    }
  }

  /**
   * Create a new job posting
   */
  async createJob(jobData: Partial<Job>): Promise<{ success: boolean; jobId?: string; error?: string }> {
    try {
      const response = await fetch(`${JOBS_API}/create`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId(),
          ...jobData
        })
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Create job error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply for a job
   */
  async applyForJob(jobId: string, message?: string): Promise<{ success: boolean; applicationId?: string; error?: string }> {
    try {
      const response = await fetch(`${JOBS_API}/apply`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId(),
          jobId,
          message
        })
      });

      return await response.json();
    } catch (error: any) {
      console.error('Apply job error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's job applications
   */
  async getMyApplications(): Promise<JobApplication[]> {
    try {
      const telegramUserId = this.getTelegramUserId();
      const response = await fetch(`${JOBS_API}/applications?telegramUserId=${telegramUserId}`);
      const data = await response.json();
      return data.applications || [];
    } catch (error) {
      console.error('Get applications error:', error);
      return [];
    }
  }

  /**
   * Get jobs posted by user's company
   */
  async getMyPostedJobs(): Promise<Job[]> {
    try {
      const telegramUserId = this.getTelegramUserId();
      const response = await fetch(`${JOBS_API}/my-jobs?telegramUserId=${telegramUserId}`);
      const data = await response.json();
      return data.jobs || [];
    } catch (error) {
      console.error('Get posted jobs error:', error);
      return [];
    }
  }

  // ==========================================================================
  // Company API
  // ==========================================================================

  /**
   * Get company profile for current user
   */
  async getMyCompany(): Promise<Company | null> {
    try {
      const telegramUserId = this.getTelegramUserId();
      const response = await fetch(`${COMPANIES_API}/profile?telegramUserId=${telegramUserId}`);
      
      if (!response.ok) return null;
      
      const data = await response.json();
      return data.company || null;
    } catch (error) {
      console.error('Get company error:', error);
      return null;
    }
  }

  /**
   * Register a new company
   */
  async registerCompany(companyData: {
    companyName: string;
    email: string;
    website?: string;
    description?: string;
    phone?: string;
  }): Promise<{ success: boolean; companyId?: string; emailSent?: boolean; error?: string }> {
    try {
      const response = await fetch(`${COMPANIES_API}/register`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId(),
          ...companyData
        })
      });

      return await response.json();
    } catch (error: any) {
      console.error('Register company error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Verify company with email code
   */
  async verifyCompany(verificationCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${COMPANIES_API}/verify`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId(),
          verificationCode
        })
      });

      return await response.json();
    } catch (error: any) {
      console.error('Verify company error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Resend company verification code
   */
  async resendVerificationCode(): Promise<{ success: boolean; emailSent?: boolean; error?: string }> {
    try {
      const response = await fetch(`${COMPANIES_API}/resend-code`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId()
        })
      });

      return await response.json();
    } catch (error: any) {
      console.error('Resend code error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update company profile
   */
  async updateCompany(updates: Partial<Company>): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${COMPANIES_API}/profile`, {
        method: 'PUT',
        headers: this.getUserHeaders(),
        body: JSON.stringify({
          telegramUserId: this.getTelegramUserId(),
          ...updates
        })
      });

      return await response.json();
    } catch (error: any) {
      console.error('Update company error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==========================================================================
  // Worker Profile API
  // ==========================================================================

  /**
   * Get worker profile for current user
   */
  async getMyProfile(): Promise<{ profile: WorkerProfile | null; hasProfile: boolean; certificationOptions: string[] }> {
    try {
      const telegramUserId = this.getTelegramUserId();
      const response = await fetch(`${WORKERS_API}/profile?telegramUserId=${telegramUserId}`);
      
      const data = await response.json();
      
      return {
        profile: data.profile || null,
        hasProfile: data.hasProfile || false,
        certificationOptions: data.certificationOptions || OILFIELD_CERTIFICATIONS
      };
    } catch (error) {
      console.error('Get profile error:', error);
      return { profile: null, hasProfile: false, certificationOptions: OILFIELD_CERTIFICATIONS };
    }
  }

  /**
   * Create worker profile
   */
  async createProfile(profileData: Partial<WorkerProfile>): Promise<{ success: boolean; profileId?: string; error?: string }> {
    try {
      // Map frontend field names to API field names
      const apiData: any = {
        telegramUserId: this.getTelegramUserId(),
        fullName: profileData.full_name,
        phone: profileData.phone,
        email: profileData.email,
        jobTitle: profileData.job_title,
        yearsExperience: profileData.years_experience,
        certifications: profileData.certifications,
        equipment: profileData.equipment,
        skills: profileData.skills,
        preferredCounties: profileData.preferred_counties,
        preferredBasins: profileData.preferred_basins,
        availability: profileData.availability,
        workTypes: profileData.work_types,
        willingToTravel: profileData.willing_to_travel,
        travelRadius: profileData.travel_radius,
        profileVisibility: profileData.profile_visibility,
        about: profileData.about
      };

      const response = await fetch(`${WORKERS_API}/profile`, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: JSON.stringify(apiData)
      });

      return await response.json();
    } catch (error: any) {
      console.error('Create profile error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update worker profile
   */
  async updateProfile(updates: Partial<WorkerProfile>): Promise<{ success: boolean; error?: string }> {
    try {
      // Map frontend field names to API field names
      const apiData: any = {
        telegramUserId: this.getTelegramUserId()
      };

      if (updates.full_name !== undefined) apiData.fullName = updates.full_name;
      if (updates.phone !== undefined) apiData.phone = updates.phone;
      if (updates.email !== undefined) apiData.email = updates.email;
      if (updates.job_title !== undefined) apiData.jobTitle = updates.job_title;
      if (updates.years_experience !== undefined) apiData.yearsExperience = updates.years_experience;
      if (updates.certifications !== undefined) apiData.certifications = updates.certifications;
      if (updates.equipment !== undefined) apiData.equipment = updates.equipment;
      if (updates.skills !== undefined) apiData.skills = updates.skills;
      if (updates.preferred_counties !== undefined) apiData.preferredCounties = updates.preferred_counties;
      if (updates.preferred_basins !== undefined) apiData.preferredBasins = updates.preferred_basins;
      if (updates.availability !== undefined) apiData.availability = updates.availability;
      if (updates.work_types !== undefined) apiData.workTypes = updates.work_types;
      if (updates.willing_to_travel !== undefined) apiData.willingToTravel = updates.willing_to_travel;
      if (updates.travel_radius !== undefined) apiData.travelRadius = updates.travel_radius;
      if (updates.profile_visibility !== undefined) apiData.profileVisibility = updates.profile_visibility;
      if (updates.about !== undefined) apiData.about = updates.about;

      const response = await fetch(`${WORKERS_API}/profile`, {
        method: 'PUT',
        headers: this.getUserHeaders(),
        body: JSON.stringify(apiData)
      });

      return await response.json();
    } catch (error: any) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload resume
   */
  async uploadResume(file: File): Promise<{ success: boolean; resumeUrl?: string; parsed?: any; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('resume', file);

      const user = telegram.getUser();
      const response = await fetch(`${WORKERS_API}/resume`, {
        method: 'POST',
        headers: {
          'X-Telegram-User-Id': user?.id?.toString() || ''
        },
        body: formData
      });

      return await response.json();
    } catch (error: any) {
      console.error('Upload resume error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Search worker profiles (for employers)
   */
  async searchWorkers(filters: WorkerSearchFilters = {}): Promise<{ profiles: WorkerProfile[]; total: number }> {
    try {
      const params = new URLSearchParams();
      params.append('telegramUserId', this.getTelegramUserId());
      
      if (filters.q) params.append('q', filters.q);
      if (filters.certifications?.length) {
        params.append('certifications', filters.certifications.join(','));
      }
      if (filters.skills?.length) {
        params.append('skills', filters.skills.join(','));
      }
      if (filters.counties?.length) {
        params.append('counties', filters.counties.join(','));
      }
      if (filters.basins?.length) {
        params.append('basins', filters.basins.join(','));
      }
      if (filters.availability) params.append('availability', filters.availability);
      if (filters.minExperience) params.append('minExperience', filters.minExperience.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${WORKERS_API}/search?${params}`);
      const data = await response.json();

      return {
        profiles: data.profiles || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('Worker search error:', error);
      return { profiles: [], total: 0 };
    }
  }

  // ==========================================================================
  // Utility Methods
  // ==========================================================================

  /**
   * Check if user is employer (has verified company)
   */
  async isEmployer(): Promise<boolean> {
    const company = await this.getMyCompany();
    return company?.status === 'verified';
  }

  /**
   * Check if user can post jobs
   */
  async canPostJobs(): Promise<{ canPost: boolean; reason?: string }> {
    const company = await this.getMyCompany();
    
    if (!company) {
      return { canPost: false, reason: 'Register your company first' };
    }
    
    if (company.status !== 'verified') {
      return { canPost: false, reason: 'Verify your company email first' };
    }
    
    return { canPost: true };
  }

  /**
   * Get certification options
   */
  getCertificationOptions(): string[] {
    return OILFIELD_CERTIFICATIONS;
  }

  /**
   * Get equipment options
   */
  getEquipmentOptions(): string[] {
    return OILFIELD_EQUIPMENT;
  }

  /**
   * Get job type options
   */
  getJobTypeOptions(): string[] {
    return JOB_TYPES;
  }

  /**
   * Get basin options
   */
  getBasinOptions(): string[] {
    return BASINS;
  }

  /**
   * Get county options
   */
  getCountyOptions(): string[] {
    return COUNTIES;
  }
}

// Export singleton instance
export const jobsService = new JobsService();
